/**
 * Modal de edição de quick replies de um playbook (Brief 4 · Gestor · Tela Quick Replies).
 *
 * REUSA o editor da Tela 1 (`TabQuickRepliesEdit` + `usePlaybookForm` + `buildPatch`
 * + `useUpdatePlaybook`) — não reconstrói (D2). Edita as quick replies do playbook
 * dono (add/remove/altera) e persiste via `PATCH /v1/playbooks/:id` com If-Match
 * (ADR-071): 409 não-destrutivo com banner + recarregar, nunca sobrescreve cego.
 * O conteúdo é envolto em `.playbooks-screen` para herdar o estilo dos componentes
 * `pb-*` da Tela 1 (escopados sob esse container).
 */

import { Modal } from '@lume/ui-components';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '../../lib/api-client';
import { TabQuickRepliesEdit } from '../playbooks/tabs/TabQuickRepliesEdit';
import { usePlaybookForm } from '../playbooks/use-playbook-form';
import { usePlaybook, useUpdatePlaybook } from '../playbooks/use-playbooks';

import type { PlaybookDto } from '../playbooks/types';

import '../playbooks/playbooks-4.css';

export function QuickReplyEditModal({
  playbookId,
  onClose,
}: {
  playbookId: string;
  onClose: () => void;
}): JSX.Element {
  const detail = usePlaybook(playbookId);

  if (detail.isLoading) {
    return (
      <Modal isOpen onClose={onClose} title="Quick replies" maxWidth="640px">
        <p className="pb-read-value pb-read-value--muted">Carregando playbook…</p>
      </Modal>
    );
  }

  if (detail.isError || detail.data === undefined) {
    return (
      <Modal isOpen onClose={onClose} title="Quick replies" maxWidth="640px">
        <p className="pb-alert" role="alert">
          Não foi possível carregar este playbook.
        </p>
      </Modal>
    );
  }

  return <QuickReplyEditModalInner playbook={detail.data} onClose={onClose} />;
}

function QuickReplyEditModalInner({
  playbook,
  onClose,
}: {
  playbook: PlaybookDto;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const form = usePlaybookForm(playbook);
  const mutation = useUpdatePlaybook(playbook.id);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [conflict, setConflict] = useState<{ currentVersion: number | null } | null>(null);

  const handleSave = (): void => {
    const patch = form.buildPatch();
    if (patch === null) {
      onClose();
      return;
    }
    setErrorMessage(null);
    mutation.mutate(
      { expectedVersion: playbook.version, body: patch },
      {
        onError: (err) => {
          if (err instanceof ApiError && err.status === 409) {
            const body = err.body as { currentVersion?: number };
            setConflict({ currentVersion: body.currentVersion ?? null });
            return;
          }
          setErrorMessage('Não foi possível salvar. Tente novamente.');
        },
        onSuccess: () => {
          onClose();
        },
      },
    );
  };

  const handleReloadConflict = (): void => {
    setConflict(null);
    setErrorMessage(null);
    void queryClient.invalidateQueries({ queryKey: ['playbook-detail', playbook.id] });
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Quick replies · “${playbook.name}”`}
      confirmLabel={mutation.isPending ? 'Salvando…' : 'Salvar'}
      cancelLabel="Fechar"
      onConfirm={handleSave}
      persistent={mutation.isPending}
      maxWidth="640px"
    >
      <div className="playbooks-screen">
        {conflict !== null && (
          <div className="pb-conflict-banner" role="alert">
            <span>
              Este playbook mudou desde que você abriu
              {conflict.currentVersion !== null
                ? ` (versão atual: v${conflict.currentVersion.toString()})`
                : ''}
              . Recarregue para ver a versão mais recente — suas alterações não foram salvas.
            </span>
            <button type="button" className="pb-btn pb-btn--sm" onClick={handleReloadConflict}>
              Recarregar
            </button>
          </div>
        )}
        {errorMessage !== null && (
          <p className="pb-alert" role="alert">
            {errorMessage}
          </p>
        )}
        <TabQuickRepliesEdit form={form} />
      </div>
    </Modal>
  );
}
